import _ from 'lodash'
import { LayoutDataItems, TypeMapping } from '@wix/thunderbolt-becky-types'

interface Range {
	min: number
	max: number
}

export type LayoutWithBreakpointRange<T extends LayoutDataItems> = {
	breakpointId: string
	breakpointRange: Partial<Range>
	layout: T
}

type LayoutWithBreakpointClosedRange<T extends LayoutDataItems> = LayoutWithBreakpointRange<T> & {
	breakpointRange: Range
}

const isEndBefore = (area1: Range, area2: Range) => area1.max <= area2.min
const isStartAfter = (area1: Range, area2: Range) => area1.min >= area2.max

const getAreasInRange = (areas: Array<Range>, range: Range) => {
	let i = 0
	while (i < areas.length && isEndBefore(areas[i], range)) {
		i++
	}

	const areasInRange = []
	while (i < areas.length && !isStartAfter(areas[i], range)) {
		areasInRange.push(areas[i])
		i++
	}

	return areasInRange
}

const getRangesBetween = (ranges: Array<Range>) => {
	const rangesBetween = []

	for (let i = 0; i < ranges.length - 1; i++) {
		if (ranges[i].max !== ranges[i + 1].min) {
			rangesBetween.push({ min: ranges[i].max, max: ranges[i + 1].min })
		}
	}

	return rangesBetween
}

const isFullyContainedInRange = (range1: Range, range2: Range) => range2.min <= range1.min && range2.max >= range1.max

const getNonCheckedAreasInRange = (areasAlreadyChecked: Array<Range>, range: Range) => {
	const areasAlreadyCheckedInRange = getAreasInRange(areasAlreadyChecked, range)
	if (areasAlreadyCheckedInRange.length === 0) {
		return [range]
	}

	if (isFullyContainedInRange(range, areasAlreadyCheckedInRange[0])) {
		return []
	}

	const areasToNotCheck = [...areasAlreadyCheckedInRange]
	if (areasAlreadyCheckedInRange[0].min > range.min) {
		areasToNotCheck.unshift({ min: areasAlreadyCheckedInRange[0].min - 1, max: areasAlreadyCheckedInRange[0].min })
		areasToNotCheck.unshift({ min: range.min, max: range.min })
	}

	if (areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max < range.max) {
		areasToNotCheck.push({
			min: areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max,
			max: areasAlreadyCheckedInRange[areasAlreadyCheckedInRange.length - 1].max + 1,
		})
		areasToNotCheck.push({ min: range.max, max: range.max })
	}

	return getRangesBetween(areasToNotCheck)
}

const getLayoutsInRangesToAdd = (
	checkedRanges: Array<Range>,
	rangeToCheck: Range,
	newRangeLayout: Omit<LayoutDataItems, 'id'>
) => {
	if (checkedRanges.length === 0) {
		return [{ breakpointRange: { min: rangeToCheck.min, max: rangeToCheck.max }, layout: newRangeLayout }]
	}

	const nonCheckedAreas = getNonCheckedAreasInRange(checkedRanges, rangeToCheck)

	return nonCheckedAreas.map((area) => ({
		breakpointRange: area,
		layout: newRangeLayout,
	}))
}

const toCloseRange = <T extends LayoutDataItems>(layoutInRange: LayoutWithBreakpointRange<T>) => ({
	...layoutInRange,
	breakpointRange: {
		...layoutInRange.breakpointRange,
		min: layoutInRange.breakpointRange.min || 0,
		max: layoutInRange.breakpointRange.max || Number.MAX_SAFE_INTEGER,
	},
	layout: layoutInRange.layout,
})
const layoutsToCloseRanges = <T extends LayoutDataItems>(layoutInRanges: Array<LayoutWithBreakpointRange<T>>) =>
	layoutInRanges.map(toCloseRange)

const toOpenRange = <T extends LayoutDataItems>(layoutInRange: LayoutWithBreakpointClosedRange<T>) => {
	const openLayoutInRange = {
		layout: layoutInRange.layout,
		breakpointRange: { ...layoutInRange.breakpointRange },
	}

	if (openLayoutInRange.breakpointRange.min === 0) {
		// @ts-ignore should be replace with expect-error editor-elements upgrade TS
		delete openLayoutInRange.breakpointRange.min
	}

	if (openLayoutInRange.breakpointRange.max === Number.MAX_SAFE_INTEGER) {
		// @ts-ignore should be replace with expect-error editor-elements upgrade TS
		delete openLayoutInRange.breakpointRange.max
	}

	return openLayoutInRange
}

const toOpenRanges = <T extends LayoutDataItems>(layoutInRanges: Array<LayoutWithBreakpointClosedRange<T>>) =>
	layoutInRanges.map(toOpenRange)

const addCheckedArea = (checkedAreas: Array<any>, areaToMarkAsChecked: { min: number; max: number }) => {
	let i = 0
	while (i < checkedAreas.length && isEndBefore(checkedAreas[i], areaToMarkAsChecked)) {
		i++
	}

	const lastIndexBefore = i

	const rangesToMerge = []
	while (i < checkedAreas.length && !isStartAfter(checkedAreas[i], areaToMarkAsChecked)) {
		rangesToMerge.push({ ...checkedAreas[i] })
		i++
	}

	const firstIndexAfterMergeArea = i

	const mergedArea =
		rangesToMerge.length === 0
			? areaToMarkAsChecked
			: {
					min: Math.min(rangesToMerge[0].min, areaToMarkAsChecked.min),
					max: Math.max(rangesToMerge[rangesToMerge.length - 1].max, areaToMarkAsChecked.max),
			  }

	return [...checkedAreas.slice(0, lastIndexBefore), mergedArea, ...checkedAreas.slice(firstIndexAfterMergeArea)]
}

const addOrMergeToLast = <T extends LayoutDataItems>(
	layoutInRanges: Array<any>,
	layoutInRangeToAdd: LayoutWithBreakpointRange<T>
) => {
	const lastAddedArea = layoutInRanges[layoutInRanges.length - 1]
	const shouldAddToLast =
		lastAddedArea &&
		(lastAddedArea.breakpointRange.max + 1 === layoutInRangeToAdd.breakpointRange.min ||
			lastAddedArea.breakpointRange.max === layoutInRangeToAdd.breakpointRange.min) &&
		_.isEqual(layoutInRangeToAdd.layout, lastAddedArea.layout)

	if (shouldAddToLast) {
		layoutInRanges.splice(layoutInRanges.length - 1, 1, {
			layout: layoutInRangeToAdd.layout,
			breakpointRange: { min: lastAddedArea.breakpointRange.min, max: layoutInRangeToAdd.breakpointRange.max },
		})
	} else {
		layoutInRanges.push(layoutInRangeToAdd)
	}
}

const mergeLayoutsInRanges = (layoutsInRanges1: string | Array<any>, layoutsInRanges2: string | Array<any>) => {
	let index1 = 0
	let index2 = 0
	const newMarkedAres: Array<never> = []

	while (index1 < layoutsInRanges1.length && index2 < layoutsInRanges2.length) {
		if (layoutsInRanges1[index1].breakpointRange.min < layoutsInRanges2[index2].breakpointRange.min) {
			addOrMergeToLast(newMarkedAres, layoutsInRanges1[index1])
			index1++
		} else {
			addOrMergeToLast(newMarkedAres, layoutsInRanges2[index2])
			index2++
		}
	}

	while (index1 < layoutsInRanges1.length) {
		addOrMergeToLast(newMarkedAres, layoutsInRanges1[index1])
		index1++
	}

	while (index2 < layoutsInRanges2.length) {
		addOrMergeToLast(newMarkedAres, layoutsInRanges2[index2])
		index2++
	}

	return newMarkedAres
}

const omitLayoutKeys = <T>(layoutInRange: { layout: T }, propKeys: Array<keyof T>) => {
	const newLayout = { ...layoutInRange.layout }
	_.forEach(propKeys, (propKey) => delete newLayout[propKey])

	return {
		...layoutInRange,
		layout: newLayout,
	}
}

function getLayoutInRangesToCheck<T extends LayoutDataItems, K extends LayoutDataItems['type']>(
	rootCompLayouts: Array<LayoutWithBreakpointRange<T>>,
	layoutType: K,
	breakpointsOrder: Array<string>
): Array<LayoutWithBreakpointClosedRange<TypeMapping[K]>> {
	const layoutInRangesToCheck = layoutsToCloseRanges(
		(rootCompLayouts.filter((layoutInRange) => layoutInRange.layout.type === layoutType) as unknown) as Array<
			LayoutWithBreakpointRange<TypeMapping[K]>
		>
	)
	const layoutsByBreakpointId = _.keyBy(layoutInRangesToCheck, 'breakpointId')
	return _([''])
		.concat(breakpointsOrder)
		.map((breakpointId) => layoutsByBreakpointId[breakpointId])
		.compact()
		.value()
}

export const extractLayoutToNonCoveredRanges = <T extends LayoutDataItems['type'], K extends keyof TypeMapping[T]>(
	layoutType: T,
	layoutPropKey: K,
	predicate: (v: TypeMapping[T][K]) => boolean,
	relatedLayoutPropKeys: Array<K> = []
) => (rootCompLayouts: Array<LayoutWithBreakpointRange<LayoutDataItems>>, breakpointsOrder: Array<string>) => {
	const layoutInRangesToCheck = getLayoutInRangesToCheck(rootCompLayouts, layoutType, breakpointsOrder)
	let layoutsInRangesToAdd: Array<LayoutWithBreakpointClosedRange<TypeMapping[T]>> = []
	let checkedAreas: Array<Range> = []

	layoutInRangesToCheck.reverse().forEach((layoutInRange) => {
		if (predicate(layoutInRange.layout[layoutPropKey])) {
			const newRangeLayout = {
				type: layoutType,
				[layoutPropKey]: layoutInRange.layout[layoutPropKey],
			}
			_.forEach(relatedLayoutPropKeys, (relatedLayoutPropKey) => {
				if (layoutInRange.layout[relatedLayoutPropKey]) {
					newRangeLayout[`${relatedLayoutPropKey}`] = layoutInRange.layout[relatedLayoutPropKey]
				}
			})

			const newLayoutsInRangesToAdd = getLayoutsInRangesToAdd(
				checkedAreas,
				layoutInRange.breakpointRange,
				newRangeLayout
			)

			layoutsInRangesToAdd = mergeLayoutsInRanges(layoutsInRangesToAdd, newLayoutsInRangesToAdd)
		}

		if (!_.isUndefined(layoutInRange.layout[layoutPropKey])) {
			checkedAreas = addCheckedArea(checkedAreas, layoutInRange.breakpointRange)
		}
	})

	return rootCompLayouts
		.map((layoutInRange) =>
			layoutInRange.layout.type === layoutType &&
			predicate((layoutInRange.layout as TypeMapping[T])[layoutPropKey])
				? omitLayoutKeys(
						layoutInRange as LayoutWithBreakpointRange<TypeMapping[T]>,
						_.concat(relatedLayoutPropKeys, [layoutPropKey])
				  )
				: layoutInRange
		)
		.concat(toOpenRanges(layoutsInRangesToAdd)) as Array<LayoutWithBreakpointRange<LayoutDataItems>>
}
